import React from 'react';
import { CancelIcon, useComponentTheme } from '@chegg-ui/theme';
import { createPortal } from 'react-dom';
import { useModalBehavior } from '../externals';
import { useMaxMatchMedia } from '../shared';
import { ConfirmationStyledModal, FullsizeContainer, ModalCenteringContainer, Title, Content, TopRightIconButton, ButtonAnimationContainer, ConfirmationModalButtons, ModalPadding, PresentationContent, getThemeConfig, NotificationBannerContainer } from '../styles';

var ConfirmationModal = function ConfirmationModal(props) {
  var _useModalBehavior = useModalBehavior(props),
      closing = _useModalBehavior.closing,
      mounted = _useModalBehavior.mounted,
      modalRef = _useModalBehavior.modalRef;

  var id = props.id,
      children = props.children,
      className = props.className,
      dataTest = props['data-test'],
      isOpen = props.isOpen,
      onClose = props.onClose,
      title = props.title,
      ariaLabel = props['aria-label'],
      ariaDescribedby = props['aria-describedby'],
      _props$hideCloseButto = props.hideCloseButton,
      hideCloseButton = _props$hideCloseButto === void 0 ? false : _props$hideCloseButto,
      _props$shouldCloseOnC = props.shouldCloseOnClickOutside,
      shouldCloseOnClickOutside = _props$shouldCloseOnC === void 0 ? false : _props$shouldCloseOnC,
      _props$hideBackground = props.hideBackgroundOverlay,
      hideBackgroundOverlay = _props$hideBackground === void 0 ? false : _props$hideBackground,
      presentationContent = props.presentationContent,
      notifBanner = props.notifBanner,
      primaryActionButton = props.primaryActionButton,
      secondaryActionButton = props.secondaryActionButton,
      _props$buttonAlignmen = props.buttonAlignment,
      buttonAlignment = _props$buttonAlignmen === void 0 ? 'vertical' : _props$buttonAlignmen,
      selector = props.selector,
      _props$rootOverflow = props.rootOverflow,
      rootOverflow = _props$rootOverflow === void 0 ? true : _props$rootOverflow,
      _props$contentOverflo = props.contentOverflow,
      contentOverflow = _props$contentOverflo === void 0 ? true : _props$contentOverflo,
      _props$size = props.size,
      size = _props$size === void 0 ? 'lg' : _props$size,
      _props$renderFullScre = props.renderFullScreenOnMobile,
      renderFullScreenOnMobile = _props$renderFullScre === void 0 ? true : _props$renderFullScre,
      _props$renderFullScre2 = props.renderFullScreenWithMarginOnMobile,
      renderFullScreenWithMarginOnMobile = _props$renderFullScre2 === void 0 ? false : _props$renderFullScre2,
      theme = props.theme;
  var componentTheme = useComponentTheme({
    name: 'Modal',
    theme: getThemeConfig(theme)
  });
  var isMobileScreen = useMaxMatchMedia('sm');
  var typographyVariant = isMobileScreen || size === 'sm' ? 'h4' : 'h2';
  var closeButtonSize = isMobileScreen ? componentTheme.sm.closeButtonSize : componentTheme[size].closeButtonSize;
  var modalContent = /*#__PURE__*/React.createElement(FullsizeContainer, {
    onClick: shouldCloseOnClickOutside ? onClose : undefined,
    className: className,
    closing: closing,
    "data-test": "".concat(dataTest ? dataTest : id, "-container"),
    hideBackgroundOverlay: hideBackgroundOverlay // consistent naming needed across modals/dialogs to avoid making this element inert, see:
    // https://www.w3.org/TR/wai-aria-practices-1.1/examples/dialog-modal/dialog.html
    ,
    id: "".concat(id, "-container")
  }, /*#__PURE__*/React.createElement(ModalCenteringContainer, {
    closing: closing
  }, /*#__PURE__*/React.createElement(ConfirmationStyledModal, {
    id: id,
    ref: modalRef,
    role: "alertdialog",
    "aria-modal": "true" // aria-label is needed only when there's no title, aria-labelledby needed only when there is
    ,
    "aria-label": title ? undefined : ariaLabel,
    "aria-labelledby": title ? "".concat(dataTest ? dataTest : id, "-title") : undefined,
    "aria-describedby": ariaDescribedby,
    onClick: function onClick(e) {
      return e.stopPropagation();
    },
    "data-test": dataTest ? dataTest : id,
    hasCloseButton: !hideCloseButton,
    size: size,
    renderFullScreenOnMobile: renderFullScreenOnMobile,
    renderFullScreenWithMarginOnMobile: renderFullScreenWithMarginOnMobile,
    customTheme: componentTheme,
    isMobileScreen: isMobileScreen
  }, /*#__PURE__*/React.createElement(ModalPadding, {
    size: size,
    rootOverflow: rootOverflow,
    isMobileScreen: isMobileScreen,
    customTheme: componentTheme
  }, presentationContent && /*#__PURE__*/React.createElement(PresentationContent, null, /*#__PURE__*/React.cloneElement(presentationContent, {
    role: 'presentation'
  })), title && /*#__PURE__*/React.createElement(Title, {
    variant: typographyVariant,
    variantType: "bold",
    tag: "h1",
    closing: closing,
    customTheme: componentTheme
  }, title), /*#__PURE__*/React.createElement(Content, {
    closing: closing,
    contentOverflow: contentOverflow
  }, children), /*#__PURE__*/React.createElement(ButtonAnimationContainer, {
    closing: closing
  }, /*#__PURE__*/React.createElement(ConfirmationModalButtons, {
    alignment: buttonAlignment,
    customTheme: componentTheme,
    isMobileScreen: isMobileScreen,
    size: size
  }, /*#__PURE__*/React.cloneElement(primaryActionButton, {
    size: 'medium'
  }), /*#__PURE__*/React.cloneElement(secondaryActionButton, {
    size: 'medium'
  }))), !hideCloseButton && /*#__PURE__*/React.createElement(TopRightIconButton, {
    onClick: onClose,
    screenReaderOnlyText: "close dialog",
    size: closeButtonSize,
    customTheme: componentTheme,
    isMobileScreen: isMobileScreen,
    modalSize: size
  }, /*#__PURE__*/React.createElement(CancelIcon, {
    id: "modalCloseIcon"
  }))), /*#__PURE__*/React.createElement(NotificationBannerContainer, {
    customTheme: componentTheme
  }, notifBanner))));

  if (!isOpen && !closing && !mounted) {
    return null;
  }

  return /*#__PURE__*/createPortal(modalContent, selector && document.querySelector(selector) || document.body);
};

ConfirmationModal.displayName = 'ConfirmationModal';
export default ConfirmationModal;